import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../modules/UserRolesManager.module.css";
import { useT } from "./TextProvider";

const USERS_ENDPOINT = "https://apitr.subxonqulov.uz/api/users/";
const USER_ROLES_ENDPOINT = "https://apitr.subxonqulov.uz/api/user-roles/";

// Роли: 2 = Учитель, 3 = Администратор
const ROLES = {
  TEACHER: 2,
  ADMIN: 3,
};

const ROLE_NAMES = {
  [ROLES.TEACHER]: "Учитель",
  [ROLES.ADMIN]: "Администратор",
};

function getStoredUser() {
  const ls = window.localStorage.getItem("auth:user");
  const ss = window.sessionStorage.getItem("auth:user");
  const raw = ls ?? ss;
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function getAnyTokenFromStorage() {
  const pick = (s) =>
    s.getItem("auth:access") ||
    s.getItem("auth:token") ||
    s.getItem("auth:key") ||
    s.getItem("auth:jwt") ||
    s.getItem("auth:auth_token") ||
    null;
  return pick(window.localStorage) || pick(window.sessionStorage);
}

function makeAuthHeader(tokenLike) {
  if (!tokenLike) return {};
  const token = String(tokenLike);
  const forcedScheme = process.env.REACT_APP_AUTH_SCHEME;
  if (forcedScheme) return { Authorization: `${forcedScheme} ${token}` };
  const looksLikeJwt = token.startsWith("eyJ");
  return looksLikeJwt
    ? { Authorization: `Bearer ${token}` }
    : { Authorization: `Token ${token}` };
}

export const UserRolesManager = () => {
  const t = useT();
  const navigate = useNavigate();
  const user = getStoredUser();

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Проверка - пользователь должен быть администратором
  useEffect(() => {
    if (!user?.id) {
      navigate("/login", { replace: true });
      return;
    }

    // Проверяем роли при загрузке
    const checkAdmin = async () => {
      try {
        const token = getAnyTokenFromStorage();
        const res = await fetch(USER_ROLES_ENDPOINT, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? makeAuthHeader(token) : {}),
          },
          credentials: "include",
        });
        if (!res.ok) {
          navigate("/", { replace: true });
          return;
        }
        const list = await res.json().catch(() => []);
        const isAdmin = Array.isArray(list)
          ? list.some(
              (r) =>
                Number(r?.user) === Number(user.id) &&
                Number(r?.role) === ROLES.ADMIN
            )
          : false;
        if (!isAdmin) {
          navigate("/", { replace: true });
        }
      } catch {
        navigate("/", { replace: true });
      }
    };

    checkAdmin();
  }, [user, navigate]);

  // Загрузка данных
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");
        const token = getAnyTokenFromStorage();
        const headers = {
          "Content-Type": "application/json",
          ...(token ? makeAuthHeader(token) : {}),
        };

        // Загружаем пользователей
        const usersRes = await fetch(USERS_ENDPOINT, {
          method: "GET",
          headers,
          credentials: "include",
        });

        if (!usersRes.ok) {
          throw new Error("Foydalanuvchilar yuklashda xatolik");
        }

        const usersData = await usersRes.json();
        const usersList = Array.isArray(usersData) ? usersData : usersData.results || [];
        setUsers(usersList);

        // Загружаем роли
        const rolesRes = await fetch(USER_ROLES_ENDPOINT, {
          method: "GET",
          headers,
          credentials: "include",
        });

        if (!rolesRes.ok) {
          throw new Error("Rollar yuklashda xatolik");
        }

        const rolesData = await rolesRes.json();
        const rolesList = Array.isArray(rolesData) ? rolesData : rolesData.results || [];
        setRoles(rolesList);
      } catch (err) {
        setError(err?.message || "Ma'lumot yuklashda xatolik");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Получить роли пользователя
  const getUserRoles = useCallback(
    (userId) => {
      return roles.filter((r) => Number(r.user) === Number(userId));
    },
    [roles]
  );

  // Проверить, есть ли у пользователя роль
  const hasRole = useCallback(
    (userId, roleId) => {
      return roles.some(
        (r) => Number(r.user) === Number(userId) && Number(r.role) === roleId
      );
    },
    [roles]
  );

  // Добавить роль
  const assignRole = async (userId, roleId) => {
    try {
      setError("");
      setSuccess("");
      const token = getAnyTokenFromStorage();
      const headers = {
        "Content-Type": "application/json",
        ...(token ? makeAuthHeader(token) : {}),
      };

      // Сначала проверяем, нет ли уже такой роли
      if (hasRole(userId, roleId)) {
        setSuccess(`Bu foydalanuvchi allaqachon bu rolga ega`);
        return;
      }

      const res = await fetch(USER_ROLES_ENDPOINT, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          user: userId,
          role: roleId,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || "Rol tayinlashda xatolik");
      }

      const newRole = await res.json();
      setRoles((prev) => [...prev, newRole]);
      setSuccess(`Rol muvaffaqiyatli tayinlandi`);
    } catch (err) {
      setError(err?.message || "Rol tayinlashda xatolik");
    }
  };

  // Удалить роль
  const removeRole = async (roleId) => {
    try {
      setError("");
      setSuccess("");
      const token = getAnyTokenFromStorage();
      const headers = {
        "Content-Type": "application/json",
        ...(token ? makeAuthHeader(token) : {}),
      };

      const res = await fetch(`${USER_ROLES_ENDPOINT}${roleId}/`, {
        method: "DELETE",
        headers,
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error("Rol olib tashlashda xatolik");
      }

      setRoles((prev) => prev.filter((r) => r.id !== roleId));
      setSuccess(`Rol muvaffaqiyatli olib tashlandi`);
    } catch (err) {
      setError(err?.message || "Rol olib tashlashda xatolik");
    }
  };

  // Фильтрация пользователей
  const filteredUsers = users.filter(
    (u) =>
      u?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Yuklash...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Foydalanuvchi rollari</h1>

      {error && <div className={styles.alert} role="alert">{error}</div>}
      {success && (
        <div className={styles.alertSuccess} role="status">
          {success}
        </div>
      )}

      <div className={styles.searchBox}>
        <input
          type="text"
          placeholder="Foydalanuvchi nomi yoki emailni qidirish..."
          className={styles.searchInput}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Foydalanuvchi qidirish"
        />
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Foydalanuvchi nomi</th>
              <th>Email</th>
              <th>Joriy rollar</th>
              <th>Rol tayinlash</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="5" className={styles.noData}>
                  Foydalanuvchi topilmadi
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => (
                <tr key={u.id}>
                  <td>{u.id}</td>
                  <td>
                    <strong>{u.username}</strong>
                  </td>
                  <td>{u.email || "-"}</td>
                  <td>
                    <div className={styles.rolesList}>
                      {getUserRoles(u.id).length === 0 ? (
                        <span className={styles.noRole}>Rol yo'q</span>
                      ) : (
                        getUserRoles(u.id).map((role) => (
                          <div key={role.id} className={styles.roleTag}>
                            <span>{ROLE_NAMES[role.role] || "Noma'lum"}</span>
                            <button
                              className={styles.removeBtn}
                              onClick={() => removeRole(role.id)}
                              title="Rol olib tashlash"
                              aria-label={`${ROLE_NAMES[role.role]} rolini olib tashlash`}
                            >
                              ✕
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={styles.actionButtons}>
                      {!hasRole(u.id, ROLES.TEACHER) && (
                        <button
                          className={styles.assignBtn}
                          onClick={() => assignRole(u.id, ROLES.TEACHER)}
                          aria-label={`${u.username} ga Ushquvchi rolini tayinlash`}
                        >
                          O'qituvchi
                        </button>
                      )}
                      {!hasRole(u.id, ROLES.ADMIN) && (
                        <button
                          className={styles.assignBtn}
                          onClick={() => assignRole(u.id, ROLES.ADMIN)}
                          aria-label={`${u.username} ga Admin rolini tayinlash`}
                        >
                          Admin
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
